import axios from 'axios';
import i18n from '../i18n';

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const client = axios.create({ baseURL: BASE_URL });

// Detect browser timezone once per session (can change mid-session if user travels,
// but reading it on every request is cheap enough that we just do it live).
const getBrowserTz = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch (e) {
    return 'UTC';
  }
};

// Attach token, app language, and user timezone on every request
client.interceptors.request.use((config) => {
  const token = localStorage.getItem('token') || sessionStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  // Use app language setting, not browser OS language
  config.headers['Accept-Language'] = i18n.language || 'en';
  // Browser timezone — used by backend to compute "today" in user's local TZ
  config.headers['X-User-Timezone'] = getBrowserTz();
  return config;
});

// Report network/timeout failures the backend never saw. Anything with a
// response is already logged server-side, so we skip it to avoid double-logging.
// Use a dynamic import to avoid a circular dependency (errorReporter imports client).
client.interceptors.response.use(
  (response) => response,
  (error) => {
    const url = error.config && error.config.url ? String(error.config.url) : '';
    // Graceful logout on a dead token: a 401 on any authenticated endpoint means
    // the stored token expired or is invalid. Clear it and bounce to /login so we
    // don't sit in a broken half-logged-in state with silently failing calls.
    // Credential flows (login/register/forgot/reset) are excluded — their 401 is a
    // wrong-password response that the form surfaces inline, not a dead session.
    if (error.response && error.response.status === 401) {
      const isCredentialFlow = /\/auth\/(login|register|forgot-password|reset-password)/.test(url);
      if (!isCredentialFlow) {
        localStorage.removeItem('token');
        localStorage.removeItem('rememberMe');
        sessionStorage.removeItem('token');
        delete client.defaults.headers.common.Authorization;
        if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
          window.location.assign('/login');
        }
      }
    }
    if (!error.response && !url.includes('client-errors')) {
      import('../lib/errorReporter')
        .then(({ reportError }) => {
          reportError({
            message: error.message || 'Network error',
            error_type: error.code || 'NetworkError',
            url: (error.config && error.config.baseURL ? error.config.baseURL : '') + url,
          });
        })
        .catch(() => {});
    }
    return Promise.reject(error);
  }
);

export default client;
